// Raw ladder numbers live in `control-size.data.mjs` — a plain ES module both
// this file and the vite plugin import without a transpile step. Edit ONLY
// that file to change size values; this file layers TypeScript types,
// `satisfies` guards, and `sizeClasses()` on top.

import { CONTROL_SIZE as _CONTROL_SIZE, CONTROL_SIZES as _CONTROL_SIZES } from './control-size.data.mjs'

// `satisfies`, not `as` — confirms the .mjs shape conforms, so a field rename
// or missing size key in the data module is a tsc error at this seam.
export const CONTROL_SIZES = _CONTROL_SIZES satisfies readonly ['xxs', 'xs', 'sm', 'md', 'lg']
export type ControlSize = (typeof CONTROL_SIZES)[number]

export type ControlRole = 'button' | 'input' | 'chip'

export interface ControlSizeSpec {
	/** Height in px — shared across all roles. Maps to --spacing-control-<size> and h-control-<size>. */
	height: number
	/** Label font-size in px — shared. Maps to --text-<size> (text-xxs new; text-xs/sm/base existing). */
	labelFont: number
	/** Label line-height in px — shared for button/chip; input derives its own from height. */
	labelLeading: number
	/** Horizontal padding in px, per role. */
	pad: Record<ControlRole, number>
	/** Gap between icon and label in px — shared across roles. */
	gap: number
	/** Icon glyph size in px, per role. */
	icon: Record<ControlRole, number>
}

export const CONTROL_SIZE = _CONTROL_SIZE satisfies Record<ControlSize, ControlSizeSpec>

// sizeClasses() maps (size, role) → space-separated Tailwind utility names;
// components spread the result into their classMap. The utilities resolve
// only once `control-size.tokens.css` is imported inside the @theme block.

// gap-<size> lands on Tailwind's default spacing scale — no custom token needed.
const GAP_CLASS: Record<ControlSize, string> = {
	xxs: 'gap-0.5',
	xs: 'gap-1',
	sm: 'gap-1.5',
	md: 'gap-2',
	lg: 'gap-2.5',
}

// text-xxs is net-new (emitted by the plugin); the rest are Tailwind defaults.
// md and sm both map to text-sm — same label scale, distinct control height.
const TEXT_CLASS: Record<ControlSize, string> = {
	xxs: 'text-xxs',
	xs: 'text-xs',
	sm: 'text-sm',
	md: 'text-sm',
	lg: 'text-base',
}

// Tailwind v4's JIT scanner extracts class names only from static strings —
// a template literal like `h-control-${size}` is invisible to it, so every
// class name below is spelled out verbatim (also true of PAD_CLASS).
const HEIGHT_CLASS: Record<ControlSize, string> = {
	xxs: 'h-control-xxs',
	xs: 'h-control-xs',
	sm: 'h-control-sm',
	md: 'h-control-md',
	lg: 'h-control-lg',
}

// The same rung as a floor rather than an exact height, for a control whose
// label is allowed to wrap. At every size the ladder's single-line content box
// (label leading or icon glyph, plus the role's vertical padding) is <= the
// rung, so a one-line control renders the rung either way and only a genuinely
// taller content box moves it. Spelled out verbatim for the same reason the
// fixed heights are: Tailwind v4 extracts class names from static strings only.
const MIN_HEIGHT_CLASS: Record<ControlSize, string> = {
	xxs: 'min-h-control-xxs',
	xs: 'min-h-control-xs',
	sm: 'min-h-control-sm',
	md: 'min-h-control-md',
	lg: 'min-h-control-lg',
}

// Width on the same rung as the height — the box an icon-only control needs so
// its target stays square instead of shrinking to its glyph. Spelled out
// verbatim for the same reason every map above is.
const WIDTH_CLASS: Record<ControlSize, string> = {
	xxs: 'w-control-xxs',
	xs: 'w-control-xs',
	sm: 'w-control-sm',
	md: 'w-control-md',
	lg: 'w-control-lg',
}

const PAD_CLASS: Record<ControlRole, Record<ControlSize, string>> = {
	button: {
		xxs: 'px-pad-button-xxs',
		xs: 'px-pad-button-xs',
		sm: 'px-pad-button-sm',
		md: 'px-pad-button-md',
		lg: 'px-pad-button-lg',
	},
	input: {
		xxs: 'px-pad-input-xxs',
		xs: 'px-pad-input-xs',
		sm: 'px-pad-input-sm',
		md: 'px-pad-input-md',
		lg: 'px-pad-input-lg',
	},
	chip: {
		xxs: 'px-pad-chip-xxs',
		xs: 'px-pad-chip-xs',
		sm: 'px-pad-chip-sm',
		md: 'px-pad-chip-md',
		lg: 'px-pad-chip-lg',
	},
}

/**
 * `height` decides whether the ladder's rung is the control's exact height or
 * only its floor. `'exact'` is the default because most controls on this ladder
 * carry a single unwrappable token and a fixed box is the simpler contract;
 * `'floor'` is for a control whose label may wrap onto a second line, where a
 * fixed height cannot hold the extra line and clips it instead.
 *
 * `box` decides what fills the inline axis. `'role'` gives the role's own
 * horizontal padding, which sizes the control to its label. `'square'` swaps
 * that padding for the rung's width, which is what a control rendering a glyph
 * alone needs: with padding only, an icon-only button collapses to its glyph and
 * stops being a target the rung's height promised.
 *
 * `'none'` on either axis emits no token for it, leaving the type scale and the
 * gap — the two the rung never makes optional, because they are what "this
 * control is on rung <size>" still means once its box is gone. It is what a
 * control reading at the rung's font size while sitting inline in its
 * neighbour's text, rather than inside a control box, asks for:
 * `<schmancy-button variant="plain">` takes neither the height nor the
 * horizontal box.
 *
 * Dropping a token is only possible HERE, where it would be emitted. A caller
 * cannot subtract one afterwards: `SchmancyElement.classMap` splits a
 * multi-token key into single-token entries and ORs them, so a token is on when
 * ANY key naming it is on, and a later key naming it `false` takes nothing away.
 */
export function sizeClasses(
	size: ControlSize,
	role: ControlRole,
	height: 'exact' | 'floor' | 'none' = 'exact',
	box: 'role' | 'square' | 'none' = 'role',
): string {
	return [
		height === 'none' ? '' : height === 'floor' ? MIN_HEIGHT_CLASS[size] : HEIGHT_CLASS[size],
		TEXT_CLASS[size],
		box === 'none' ? '' : box === 'square' ? WIDTH_CLASS[size] : PAD_CLASS[role][size],
		GAP_CLASS[size],
	]
		.filter(Boolean)
		.join(' ')
}

/**
 * Height/horizontal-padding/font-size/line-height for a (size, role) pair, as
 * inline-style values (px strings) — the single source `<schmancy-input>` derives
 * its box geometry from. `lineHeight` is intentionally set to `height` (not
 * `labelLeading`) — this is the role-aware vertical-centering mechanism for a
 * single-line text control; do not swap it for the shared label line-height.
 * Any consumer that must render the same box a real control of this (size, role)
 * would — e.g. schmancy-revolute's card-field wrapper standing in for an
 * `<schmancy-input>` — calls this instead of hand-copying `CONTROL_SIZE` numbers,
 * so a ladder change in `control-size.data.mjs` cascades to both automatically.
 */
export function controlSizeStyles(
	size: ControlSize,
	role: ControlRole = 'input',
): { height: string; padding: string; fontSize: string; lineHeight: string } {
	const spec = CONTROL_SIZE[size]
	return {
		height: spec.height + 'px',
		padding: '0 ' + spec.pad[role] + 'px',
		fontSize: spec.labelFont + 'px',
		lineHeight: spec.height + 'px',
	}
}
